import { MarkHearthArch as HearthMark } from "../components/brand/Marks";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { ShieldCheck, Loader2, XCircle, KeyRound, Check, Minus, ArrowRight, FolderArchive, Users, HeartHandshake } from "lucide-react";
import axios from "axios";
import { inviteInfo, acceptInvite } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NoIndex } from "../components/Seo";

const ROLES = {
  owner: { blurb: "Full access to everything, including members and billing.", can: ["See, add and edit every document", "Manage family members and billing"], cant: [] },
  partner: {
    blurb: "You're a full partner in keeping the household organized.",
    can: ["Browse every document and the estate plan", "Upload, snap or forward new documents", "Edit reminders, checklists and the bereavement pack", "Import passwords and reveal them when needed"],
    cant: ["Invite or remove family members", "Change billing or the owner's settings"],
  },
  executor: {
    blurb: "You've been trusted to know where everything is — when it matters most.",
    can: ["Read every document, the estate plan and the library", "Download the executor summary and bereavement pack", "See bank & policy inventories (balances hidden)"],
    cant: ["Add, edit or delete anything", "Reveal stored passwords (the owner can grant emergency access)"],
  },
};
const STEPS = [
  { Icon: FolderArchive, text: "Everything is already filed — receipts, policies, wills, manuals — searchable by AI." },
  { Icon: HeartHandshake, text: "The estate plan and bereavement pack explain what to do and who to call." },
  { Icon: Users, text: "Each family member has a private PIN; nothing is shared between profiles." },
];

const WelcomePanel = ({ info, onContinue }) => {
  const r = ROLES[info.role];
  return (
    <motion.div key="welcome" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} className="text-left" data-testid="join-welcome">
      <p className="text-xs uppercase tracking-widest font-mono text-primary">You're invited</p>
      <h1 className="mt-1 font-serif text-3xl leading-snug" data-testid="join-title">
        {info.advisor_created
          ? <>Welcome, {info.name.split(" ")[0]}. {info.inviter} set up <span className="text-primary">{info.household_name || "your family vault"}</span> for you — you're the owner.</>
          : <>Welcome, {info.name.split(" ")[0]}. {info.inviter} added you to the {info.owner_name ? `${info.owner_name.split(" ")[0]} ` : ""}family vault as <span className="text-primary">{info.role_label}</span>.</>}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">{r.blurb}</p>
      <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-4">
          <div className="text-[10px] uppercase tracking-wider font-mono text-emerald-700 mb-2">You can</div>
          <ul className="space-y-1.5" data-testid="join-can-list">{r.can.map((c) => <li key={c} className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {c}</li>)}</ul>
        </div>
        {r.cant.length > 0 && (
          <div className="rounded-2xl border border-border bg-muted/40 p-4">
            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground mb-2">Not this role</div>
            <ul className="space-y-1.5 text-muted-foreground" data-testid="join-cant-list">{r.cant.map((c) => <li key={c} className="flex gap-2"><Minus className="h-4 w-4 shrink-0 mt-0.5" /> {c}</li>)}</ul>
          </div>
        )}
      </div>
      <ul className="mt-5 space-y-2.5">
        {STEPS.map((s) => <li key={s.text} className="flex items-start gap-3 text-sm text-muted-foreground"><span className="h-8 w-8 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><s.Icon className="h-4 w-4" /></span><span className="pt-1.5">{s.text}</span></li>)}
      </ul>
      <div className="mt-5 text-xs text-muted-foreground" data-testid="join-vault-facts">{info.document_count} documents filed · {info.member_count} {info.member_count === 1 ? "member" : "members"} so far</div>
      <Button data-testid="join-continue-btn" onClick={onContinue} className="mt-6 w-full h-11 rounded-full bg-primary hover:bg-primary/90 gap-2">Choose my PIN <ArrowRight className="h-4 w-4" /></Button>
    </motion.div>
  );
};

const PinPanel = ({ info, onBack, onDone }) => {
  const [name, setName] = useState(info.name);
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!/^\d{4,6}$/.test(pin)) return toast.error("PIN must be 4–6 digits");
    if (pin !== confirm) return toast.error("PINs don't match");
    setBusy(true);
    try {
      await onDone(pin, name.trim() || undefined);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept the invite");
      setBusy(false);
    }
  };
  return (
    <motion.div key="pin" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} className="text-center" data-testid="join-pin-step">
      <p className="text-xs uppercase tracking-widest font-mono text-primary">Last step</p>
      <h1 className="mt-1 font-serif text-2xl">Choose your private PIN</h1>
      <p className="mt-2 text-sm text-muted-foreground">4–6 digits. Only you know it; the owner can't see it, but can reset it for you.</p>
      <div className="mt-6 space-y-3 text-left">
        <Input data-testid="join-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="bg-background" />
        <Input data-testid="join-pin-input" inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} placeholder="Choose a PIN (4–6 digits)" className="bg-background" />
        <Input data-testid="join-pin-confirm-input" inputMode="numeric" maxLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value.replace(/\D/g, ""))} placeholder="Confirm PIN" className="bg-background" />
        <Button data-testid="join-submit-btn" onClick={submit} disabled={busy} className="w-full gap-2 bg-primary hover:bg-primary/90 rounded-full">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Set my PIN & enter
        </Button>
        <button data-testid="join-back-btn" onClick={onBack} className="w-full text-xs text-muted-foreground hover:text-primary">← Back to what my role means</button>
      </div>
      <p className="mt-4 text-[11px] text-muted-foreground flex items-center justify-center gap-1"><ShieldCheck className="h-3 w-3" /> Your PIN is private — only you know it.</p>
    </motion.div>
  );
};

export default function JoinVault() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState("welcome");

  useEffect(() => {
    inviteInfo(token).then(setInfo).catch((e) => setError(e?.response?.data?.detail || "This invite link is not valid"));
  }, [token]);

  const accept = async (pin, name) => {
    const { token: session, member } = await acceptInvite(token, pin, name);
    axios.defaults.headers.common["X-Vault-Token"] = session;
    if (member?.id) localStorage.setItem("hv_last_member", member.id);
    toast.success("You're in! Opening the vault…");
    setTimeout(() => navigate("/vault", { replace: true }), 600);
  };

  const blocked = error || info?.expired || info?.accepted;

  return (
    <div className="min-h-screen bg-background grid place-items-center px-6 py-10">
      <NoIndex title="Join the vault" />
      <Toaster position="top-center" richColors />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} data-testid="join-page" className={`w-full ${step === "welcome" && info && !blocked ? "max-w-xl" : "max-w-sm"} bg-card border border-border rounded-3xl p-8 text-center transition-[max-width]`}>
        <div className="h-14 w-14 mx-auto rounded-2xl bg-primary text-primary-foreground grid place-items-center mb-5"><HearthMark className="h-7 w-7" /></div>
        {!info && !error && <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mt-6" />}
        {blocked && (
          <>
            <XCircle className="h-10 w-10 text-amber-500 mx-auto mt-2" />
            <h1 className="mt-4 font-serif text-2xl" data-testid="join-blocked-title">{error ? "Invite not found" : info.accepted ? "Already used" : "Invite expired"}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{error || "Ask the vault owner to send you a fresh invite link."}</p>
          </>
        )}
        {info && !blocked && (
          <AnimatePresence mode="wait">
            {step === "welcome" ? <WelcomePanel info={info} onContinue={() => setStep("pin")} /> : <PinPanel info={info} onBack={() => setStep("welcome")} onDone={accept} />}
          </AnimatePresence>
        )}
      </motion.div>
    </div>
  );
}
